/**
 * Mirrors the REST/WS search envelope from `POST /api/dimension`, `POST /api/stream`,
 * `POST /api/analysis`, and slice rows returned by the API (aligned with services/api
 * `Slice` types).
 *
 * All read paths consume the same `Query` envelope from `apiTypes.ts`; this file
 * just adds the response shapes the API returns.
 */

import type { Slice } from './apiTypes'

export type SeverityTier = 0 | 1 | 2
export type SeverityLabel = 'green' | 'yellow' | 'red'

export type MetricSummary = {
  tier: SeverityTier | null
  displayValue: number | null
  mean?: number | null
  min?: number | null
  max?: number | null
  p50?: number | null
  p95?: number | null
  p99?: number | null
  cov?: number | null
  tierReason?: string | null
}

export type SliceMetrics = {
  demuxInRate: MetricSummary
  demuxToDecodeLag: MetricSummary
  renderRatio: MetricSummary
  downloadKbps: MetricSummary
  jitter: MetricSummary
  packetDelay: MetricSummary
  dropFrame: MetricSummary
  pFrameDelay: MetricSummary
  iFrameDelay: MetricSummary
  /** Decode buffer level (window min). */
  bufferLevel?: MetricSummary
  /** Rendered fps vs nominal. */
  fps?: MetricSummary
  iFrameCount?: MetricSummary
  pFrameCount?: MetricSummary
  bufferOverRun?: MetricSummary
  bufferUnderRunMs?: MetricSummary
  avgFrameBytes?: MetricSummary
  /** Optional decode-queue proxy when present. */
  decodeQueue?: MetricSummary
  mediaLatency?: MetricSummary
}

export type DimensionSliceNode = {
  kind: 'group'
  type: string
  label: string
  value: string
  windowStartMs: number
  windowEndMs: number
  tickCount: number
  usersTotal: number
  totalStreams: number
  /** Distinct streams scored critical / warning / good in this slice. */
  criticalCount?: number
  warningCount?: number
  goodCount?: number
  totalObservationMs: number
  excludedCount: number
  avgSeverity: SeverityTier | null
  severity: SeverityLabel | string | null
  worstMetric: string | null
  worstMetricSeverity: SeverityTier | null | null
  metrics: SliceMetrics
}

export type StreamSliceNode = {
  kind: 'stream'
  type: 'stream'
  streamId: string
  label: string
  value: string
  /** When listed under a dimension drill (e.g. country / US). */
  parentSliceType?: string | null
  parentSliceValue?: string | null
  windowStartMs: number
  windowEndMs: number
  tickCount: number
  usersTotal: number
  totalStreams: number
  criticalCount?: number
  warningCount?: number
  goodCount?: number
  totalObservationMs: number
  excludedCount: number
  avgSeverity: SeverityTier | null
  severity: SeverityLabel | string | null
  worstMetric: string | null
  worstMetricSeverity: SeverityTier | null
  metrics: SliceMetrics
  studio?: string | null
  continent?: string | null
  country?: string | null
  region?: string | null
  city?: string | null
  browser?: string | null
  os?: string | null
  deviceType?: string | null
  platform?: string | null
  connectionType?: string | null
  cdnEdge?: string | null
  ip?: string | null
  sourceUrl?: string | null
  status?: 'online' | 'idle' | 'recently-offline' | null
  lastSeenMs?: number | null
  /** Catalog inventory type (millicast / flv); null if uncatalogued. */
  streamType?: string | null
  catalogUrl?: string | null
  targetDevice?: string | null
  game?: string | null
  brand?: string | null
}

export type SearchSliceNode = DimensionSliceNode | StreamSliceNode

/** Response body for `POST /api/dimension`, `POST /api/stream`, and WS `snapshot`. */
export type SearchSliceEnvelope = {
  ts: number
  fromMs: number
  toMs: number
  dimension?: string
  view?: 'dimension' | 'stream' | 'analysis' | 'world'
  slice?: Slice
  nodes: SearchSliceNode[]
  /** Active data key that produced this envelope (REST fetch). */
  queryKey?: string
  /** Optional KPIs when the API includes them */
  critical?: number
  warnings?: number
  total_streams?: number
  total_users?: number
}

/** Response body for `POST /api/analysis`. Same nodes, with extra mode metadata. */
export type AnalysisSearchEnvelope = SearchSliceEnvelope & {
  mode?: 'dimension' | 'stream'
}

export function sliceRowKey(n: SearchSliceNode): string {
  if (n.kind === 'stream') {
    const sn = n as StreamSliceNode
    const pt = sn.parentSliceType ?? 'global'
    const pv = sn.parentSliceValue ?? '*'
    return `${n.kind}:${n.streamId}:${pt}:${pv}`
  }
  return `${n.kind}:${n.type}:${n.value}`
}

export function mergeSliceNodes(existing: SearchSliceNode[], incoming: SearchSliceNode[]): SearchSliceNode[] {
  const m = new Map(existing.map((n) => [sliceRowKey(n), n]))
  for (const n of incoming) m.set(sliceRowKey(n), n)
  return [...m.values()]
}

/** List rollups aligned with `services/api` `computeKpisFromNodes` (search envelopes). */
export function computeSearchEnvelopeKpisFromNodes(
  nodes: SearchSliceNode[],
): Pick<SearchSliceEnvelope, 'total_users' | 'total_streams' | 'warnings' | 'critical'> {
  let total_users = 0
  let total_streams = 0
  let warnings = 0
  let critical = 0
  for (const n of nodes) {
    total_users += Number(n.usersTotal ?? 0)
    total_streams += Number(n.totalStreams ?? 0)
    if (n.avgSeverity === 1) warnings++
    else if (n.avgSeverity === 0) critical++
  }
  return { total_users, total_streams, warnings, critical }
}

export function computeSnapshotTimeBoundsFromNodes(nodes: SearchSliceNode[]): { fromMs: number; toMs: number } {
  if (!nodes.length) {
    const t = Date.now()
    return { fromMs: t, toMs: t }
  }
  let fromMs = Infinity
  let toMs = -Infinity
  for (const n of nodes) {
    const a = Number(n.windowStartMs)
    const b = Number(n.windowEndMs)
    if (Number.isFinite(a) && a < fromMs) fromMs = a
    if (Number.isFinite(b) && b > toMs) toMs = b
  }
  if (!Number.isFinite(fromMs)) fromMs = Date.now()
  if (!Number.isFinite(toMs)) toMs = fromMs
  return { fromMs, toMs }
}
